<template>
  <TableList
    v-rect-loading="loading"
    class="post-applicant-invitation-list"
    row-key="id"
    hideOnClick
    :title="$route.meta.title"
    :listData="listData"
    :tableColumns="tableColumns"
    :disabledActionBtn="disabledActionBtn"
    :actionBtnTooltip="actionBtnTooltip"
    :actionBtn="actionBtn"
    :showCreateBtn="false"
    :pagination="pagination"
    @pagination-change="handlePaginationChange"
  >
    <template #filter>
      <span class="filter-keywords">
        <el-input
          v-model="searchCondition.keywords"
          clearable
          :placeholder="$t(`${i18nPrefix}.label.keywordsSearch`)"
          @input="handleKeywordsChange"
        />
      </span>
      <span class="filter-status">
        <el-select v-model="searchCondition.status" clearable @change="handleSearch">
          <el-option
            v-for="item in statusList"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          >
          </el-option>
        </el-select>
      </span>
    </template>
    <template #btn>
      <el-button v-rect-loading="exportLoading" class="export-btn" @click="onExportClick">{{ $t(`${i18nPrefix}.label.export`) }}</el-button>
    </template>
    <template #job="{ row }">
      <span v-if="row.job && row.is_candidate_apply_job" class="can-click" @click="onJobClick(row)">
        {{ row.job.name }}
      </span>
      <template v-else>{{ row.job && row.job.name ? row.job.name: '-'}}</template>
    </template>
    <template #candidate="{ row }">
      <span v-if="row.candidate && row.job && row.is_candidate_apply_job" class="can-click" @click="onCandidateClick(row)">
        {{ row.candidate && row.candidate.name }}
      </span>
      <template v-else>{{ row.candidate && row.candidate.name ? row.candidate.name: '-' }}</template>
    </template>
  </TableList>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import { addI18nProperty } from '@/i18n'
import { formatDate } from '@/components/filters'

export default {
  components: {
    TableList
  },
  data () {
    return {
      loading: false,
      i18nPrefix: 'admin.company.postApplicant',
      searchCondition: {
        keywords: null,
        status: null
      },
      listData: [],
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      exportLoading: false
    }
  },
  computed: {
    hasOnlyActionOnMyCandidatePermission () {
      return this.hasAnyPermission('PERM_ONLY_ACTION_ON_MY_CANDIDATE')
    },
    statusList () {
      return [
        {
          label: this.$t(`${this.i18nPrefix}.label.statusList.all`),
          value: '0'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.statusList.invited`),
          value: '1'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.statusList.finished`),
          value: '2'
        }
      ]
    },
    tableColumns () {
      return [
        {
          label: this.$t(`${this.i18nPrefix}.label.invitationDate`),
          dataKey: 'created_time',
          formatter: (data) => formatDate(data)
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.postApplicantDate`),
          dataKey: 'finished_time',
          formatter: (data) => formatDate(data)
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.company`),
          dataKey: 'company',
          formatter: (data) => data.i18nName
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.job`),
          dataKey: 'job',
          slotName: 'job'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.microsite`),
          dataKey: 'micro_site_page',
          formatter: (data) => data && data.name ? data.name : '-'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.questionnaire`),
          dataKey: 'questionnaire',
          formatter: (data) => data && data.name ? data.name : '-'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.recruiter`),
          dataKey: 'recruiter_full_name'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.candidate`),
          dataKey: 'candidate',
          slotName: 'candidate'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.status`),
          dataKey: 'status',
          formatter: (data) => {
            if (data === 1) {
              return this.$t(`${this.i18nPrefix}.label.statusList.invited`)
            } else if (data === 2) {
              return this.$t(`${this.i18nPrefix}.label.statusList.finished`)
            } else if (data === 3) {
              return this.$t(`${this.i18nPrefix}.label.statusList.cancelled`)
            }
          }
        }
      ]
    },
    actionBtn () {
      return {
        notice: {
          show: (rowData) => rowData.status === 1,
          onclick: this.onNoticeClick,
          labelI18nKey: `${this.i18nPrefix}.label.operationBtn.notice`
        },
        view: {
          show: (rowData) => rowData.status === 2 && rowData.is_candidate_apply_job,
          onclick: this.onCandidateClick,
          labelI18nKey: `${this.i18nPrefix}.label.operationBtn.view`
        }
      }
    }
  },
  created () {
    this.fetchListData(true)
  },
  methods: {
    ...mapActions('postApplicantManagement', ['searchInvitation', 'updateInvitation', 'putInvitation', 'exportInvitation'
    ]),
    async fetchListData (reload) {
      this.loading = true
      if (reload) {
        this.pagination.page = 1
        this.pagination.total = 0
      }
      try {
        const resp = await this.searchInvitation({
          keywords: this.searchCondition.keywords,
          status: this.searchCondition.status,
          page_num: this.pagination.page,
          page_size: this.pagination.pageSize
        })
        if (resp) {
          this.listData = _.map(resp.invitations, invitation => {
            addI18nProperty(invitation.company, 'i18nName', 'cn_name', 'name')
            return invitation
          })
          this.pagination.total = resp.total_count
        }
      } finally {
        this.loading = false
      }
    },
    disabledActionBtn (rowData) {
      return this.hasOnlyActionOnMyCandidatePermission && !rowData.job.is_my_job
    },
    actionBtnTooltip (rowData) {
      return this.disabledActionBtn(rowData) ? this.$t('admin.application.applicantList.message.disabledActionCandidateInJob') : null
    },
    async onExportClick () {
      this.exportLoading = true
      try {
        const resp = await this.exportInvitation(this.searchCondition)
        if (resp) {
          window.location.href = this.$DjangoMediaUrl(resp.file)
        }
      } finally {
        this.exportLoading = false
      }
    },
    onJobClick (rowData) {
      const job = rowData.job
      if (job) {
        const url = `/v-admin/job/${job.id}/application`
        window.open(url)
      }
    },
    async onNoticeClick (rowData) {
      this.loading = true
      const job = rowData.job
      const id = rowData.id
      const candidate = rowData.candidate
      try {
        if (job && candidate) {
          const invitation = {
            id: id,
            is_retry: true,
            job_id: job.id,
            candidate_ids: [candidate.id]
          }
          await this.updateInvitation(invitation)
          this.$message.success(this.$t(`${this.i18nPrefix}.label.noticeSuccessMessage`))
        }
      } finally {
        this.loading = false
      }
    },
    async onCancelClick (rowData) {
      this.loading = true
      const id = rowData.id
      try {
        const invitation = {
          id: id,
          status: 3
        }
        await this.putInvitation(invitation)
        this.$message.success('common.message.optionSuccess')
      } finally {
        this.loading = false
      }
      this.fetchListData()
    },
    onCandidateClick (rowData) {
      const job = rowData.job
      const candidate = rowData.candidate
      if (job && candidate) {
        const url = `/v-admin/application/profile/resume?&candidate_id=${candidate.id}&job_id=${job.id}`
        window.open(url)
      }
    },
    handlePaginationChange () {
      this.fetchListData()
    },
    handleKeywordsChange () {
      if (!this.keywordsSearchDebounce) {
        this.keywordsSearchDebounce = _.debounce(this.handleSearch, 1000)
      }
      this.keywordsSearchDebounce()
    },
    handleSearch () {
      this.fetchListData()
    }
  }
}
</script>

<style lang="scss" scoped>
.post-applicant-invitation-list {
  .export-btn {
    ::v-deep {
      .el-loading-mask {
        .el-loading-spinner {
          margin-top: -14px;

          .spinner {
            height: 26px;
          }
        }
      }
    }
  }

  .can-click {
    cursor: pointer;
    color: #0096cc;

    &:hover {
      color: #33abd6;
    }
  }
}
</style>
